import { AlertCircle, ArrowLeftRight } from '@tamagui/lucide-icons';
import React from 'react';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { Card, H2, XStack, YStack, getTokenValue, Token, CardProps, Text } from 'tamagui';
import { Toggle, tokens } from 'tamagui-config';

export interface WidgetCardProps extends CardProps {
  title: string;
  children?: React.ReactNode;
  isDraggable?: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  error?: boolean;
  gap?: string;
  enabled?: boolean;
  onEnabledChange?: (enabled: boolean) => void;
  timestamp?: string;
  hideArrowIcon?: boolean;
}

export const WidgetCard = ({
  title,
  children,
  isDraggable = false,
  dragHandleProps,
  error,
  gap = '$5',
  enabled = true,
  onEnabledChange,
  timestamp,
  hideArrowIcon = false,
  ...rest
}: WidgetCardProps): JSX.Element => {
  const gradientStartColor = getTokenValue('$widgetCardGradientStart' as Token, 'color');
  const gradientEndColor = getTokenValue('$widgetCardGradientEnd' as Token, 'color');

  return (
    <Card
      width={600}
      height="100%"
      backgroundColor="$background"
      borderRadius="$4"
      borderWidth={2}
      borderColor="rgba(255, 255, 255, 0.05)"
      backgroundImage={`linear-gradient(149deg, ${gradientStartColor} 0%, ${gradientEndColor} 100%)`}
      padding="$5"
      marginRight="$5"
      overflow="hidden"
      {...rest}>
      <YStack gap={gap} flex={1}>
        <XStack justifyContent="space-between" alignItems="center">
          <XStack gap="$3.5" alignItems="center" flex={1} minWidth={0}>
            {isDraggable && !hideArrowIcon && (
              <div {...dragHandleProps} style={{ cursor: 'grab' }}>
                <ArrowLeftRight size={40} color="$color" />
              </div>
            )}
            <H2 size="$6" fontWeight="$5" flex={1} color="$color" numberOfLines={1} ellipse>
              {title}
            </H2>
          </XStack>
          <XStack gap="$3" alignItems="center" flexShrink={0}>
            {timestamp && !isDraggable && (
              <Text fontSize="$5" color={tokens.color.white10}>
                {timestamp}
              </Text>
            )}
            {isDraggable && onEnabledChange && (
              <Toggle checked={enabled} onCheckedChange={onEnabledChange} />
            )}
            {error && <AlertCircle size={40} color={tokens.color.warningRed} />}
          </XStack>
        </XStack>
        {children}
      </YStack>
    </Card>
  );
};
